/**
 * **BATTLE EVOLUTION — Suikamon cards collected DURING a fight, and the third axis of scaling.**
 *
 * `levelScaling.ts` answers "how strong is this species at this LEVEL"; `battleModeTuning.ts`
 * answers "and how does this MODE change that". This module answers the third question, and it
 * is the only one whose answer changes while the fight is happening: **"and how many Suikamon
 * cards has this fighter taken off its opponent so far?"**
 *
 * Win an exchange and you take a **Suikamon card**. Three of them and you evolve mid-battle;
 * six and you evolve again. An evolution **adds a fifth of a health bar and +10% damage**, and
 * the second one adds another fifth — the HP going up *is* a heal, which is what makes an
 * evolution land as a moment rather than as a number in a tooltip. **The whole of the growth is handed
 * back as HP** ({@link EVOLUTION_HEAL_SHARE}), so a fighter's share of its own bar never falls
 * on the beat it evolves.
 *
 * **HP and damage are two dials, not one** ({@link EVOLUTION_RANK_HP_BONUS},
 * {@link EVOLUTION_RANK_DAMAGE_BONUS}), because they buy different things: the HP is what makes
 * an evolution *survive*, the damage is what makes it *land*, and a single shared percentage
 * could never be moved for one without moving the other.
 *
 * **Every Suikamon starts every battle at Evolution 1 and ends it back there.** Nothing here is
 * stored, nothing is spent, and nothing survives the fight: the count lives on `PlayerState`,
 * which is already serialised whole into `pvp_battles.battle_state`. That is the whole reason
 * this needs no schema of any kind — a battle already carries its own state, and this is more
 * of it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **THIS IS NOT THE PET'S EVOLUTION STAGE, and the two must never be merged.**
 *
 * A Suikamon's `stage` is permanent, earned by LEVEL (`STAGE_LEVEL_DISTRIBUTION` =
 * `[0, 2, 8, 14, 25, 30]`), written on-chain, and purely cosmetic — `levelScaling.ts` is
 * explicit that level is the only stat scaler. A **battle evolution** is temporary, earned from
 * the deck, lives only in `battle_state`, and is the one thing that *does* change the numbers.
 *
 * **A battle evolution DOES change the picture — and only the picture.** The fighter wears its
 * own next stage's art for the rest of the fight ({@link getBattleFormStage}), which is what
 * makes a rung land as a transformation rather than as a number. It writes NOTHING: `stage` on
 * the pet is untouched, the chain is untouched, and the collection screen still shows the
 * Suikamon you actually own the moment the battle ends.
 *
 * Keeping them apart is not pedantry: `speciesConfig.ts` states that no pet may ever be "ahead
 * of its level" because the Move contract's evolution check relies on that ordering. A mechanic
 * that wrote `stage` would be a mechanic that can brick a pet's on-chain evolution.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **WHY GROWING HP THIS MUCH IS FINE HERE WHEN IT WOULD NOT BE AS PERMANENT PROGRESS.**
 *
 * The usual objection to numbers this size is that HP and damage multiply together into combat
 * power: **×1.2 HP with +10% damage is a ×1.32 swing at Evolution 2, and ×1.4 with +20% is
 * ×1.68 at Evolution 3.** As a permanent, account-wide bonus against opponents who do not have
 * it, that is not merely outside the 40-60% band criterion 1 measures pay-to-win in — it would
 * be the whole game.
 *
 * **None of that applies to a symmetric mechanic that resets.** Both fighters climb the same
 * ladder from the same floor by the same rule, in the same fight, and both are back at
 * Evolution 1 next battle. There is no collection to out-grind and nothing to buy. What the
 * swing actually buys is a **second act**: a fighter who has held six cards through the pitch
 * is fighting a longer, louder fight than the one it started, which is what an evolution is
 * supposed to feel like.
 *
 * **The split is what keeps it from being a one-shot.** Most of it is on the HP bar, so the
 * rung mostly buys ROUNDS; the damage moves at HALF that rate, so an evolved fighter takes
 * longer to kill rather than killing instantly. A ladder that paid ×2 on damage instead would
 * end fights on the turn it landed and delete the multi-turn shape each species' `baseHp` holds.
 *
 * **The real risk is the opposite one — a snowball.** The side that is already winning is the
 * side collecting cards, and `BattleCardsPage` states as a design value that a fight you are
 * losing should keep "enough rounds to turn around". Two things bound it deliberately: the
 * ladder tops out at two evolutions, and every evolution comes with a **heal**, which is worth
 * proportionally more to the fighter on low HP. It still needs measuring — see
 * `requirements/BATTLE_EVOLUTION.md` §6.
 *
 * Pure data and pure functions. No I/O, no RNG, no storage.
 *
 * Design: `requirements/BATTLE_EVOLUTION.md`
 */

import { hasScalableSpecialEffect } from './levelScaling.js';
import type { SpeciesCombatConfig } from './petModels.js';

// ============================================================================
// The ladder
// ============================================================================

/**
 * How far up the in-battle ladder a fighter has climbed. **Every Suikamon starts every battle
 * at Evolution 1**, holding no cards and fighting at exactly its printed numbers.
 *
 * Rank 1 is not something a fighter is missing — it is the floor both sides start from and
 * everything else is measured against.
 */
export type BattleEvolutionRank = 1 | 2 | 3;

/** The floor every fight starts at, on both sides, always. */
export const EVOLUTION_RANK_MIN: BattleEvolutionRank = 1;

/**
 * The ceiling. **Two evolutions, not four.**
 *
 * A cards battle runs a handful of exchanges, so the ladder has to fit inside one. Four rungs
 * would mean the top one is only ever reached in a fight that had already been won several
 * turns earlier — a reward for a decision nobody was still making.
 */
export const EVOLUTION_RANK_MAX: BattleEvolutionRank = 3;

/** Every rung, weakest first. */
export const EVOLUTION_RANK_ORDER: readonly BattleEvolutionRank[] = [1, 2, 3];

/**
 * **Cards per evolution: three.** The number the mechanic is named after.
 *
 * The cost is **LINEAR** — 0, 3, then 6 — and that is the one place this deliberately departs
 * from the auto-battler ladder it borrows from, where nine copies make a three-star. Nine is
 * the right number when the collecting happens across a thirty-round match; inside a fight this
 * length it is a rung nobody would ever stand on. Six is reachable by a fighter who is winning,
 * and unreachable by one who is not, which is exactly the line the rung is for.
 */
export const EVOLUTION_CARDS_PER_STEP = 3;

/** Cards needed, in total, to stand on a rung: 0, 3, 6. */
export function evolutionCardsForRank(rank: BattleEvolutionRank): number {
  return EVOLUTION_CARDS_PER_STEP * (rank - 1);
}

/**
 * **WHAT A RUNG PAYS ON THE HP BAR — the bigger of the two dials, by a long way.**
 *
 * Read as a bonus on the fighter's OWN starting pool: **Evolution 2 adds a fifth of a bar,
 * Evolution 3 another fifth** — ×1.2 then ×1.4. Applied to max HP and, through
 * {@link growMaxHpForEvolution}, to current HP as well: the whole of the growth is restored
 * ({@link EVOLUTION_HEAL_SHARE}), so a rung hands back **a fifth of the fighter's opening bar on
 * the spot** and a fighter that was full is still full.
 *
 * **The steps are EVEN — +20% of base, then another +20%** — because every rung costs the
 * same {@link EVOLUTION_CARDS_PER_STEP} cards. A ladder whose rungs cost the same and pay
 * differently is a ladder where one rung is the only one worth standing on.
 *
 * **IT WAS ×2 / ×3, AND IT CAME DOWN WHEN THE HEAL WENT UP — the same rung, priced differently.**
 * While only half the growth was healed, ×2 handed back half a bar; with the whole growth healed
 * (the owner's rule: the HP an evolution grants is HP you HAVE) ×2 handed back a WHOLE one, and
 * criterion `13` — the winner's lowest bar — went out of band at 63.8% against a 60% ceiling: the
 * fighter collecting the cards was never pushed. At ×1.5 the rung pays the same HP on the beat as
 * it always did, `13` reads 59.4%, and the fill still cannot retreat. **`13`'s own named lever was
 * tried first and did not earn it** — `PRESSURE_PER_ROUND` at +40% bought 1.7 points and cost the
 * texture of every hit. Row 43 of `docs/BATTLE_CARDS_TUNING_LOG.md` carries the paired numbers,
 * including what it cost (`11`, the recover suit's commit share) and the check that stops the pool
 * being cut further: **`10`'s burst p99, at 65% of a bar against a 70% ceiling.**
 *
 * **WHY A NUMBER THIS LARGE IS DEFENSIBLE HERE.** It is enormous by the standards of anything
 * permanent, and it would be indefensible as one — see the module header. What makes it a
 * different mechanic is that it is **symmetric and it resets**: both fighters climb the same
 * ladder from the same floor by the same rule inside the same fight, and both are back at
 * Evolution 1 next battle. Nothing is collected, nothing is bought, and criterion 1's
 * pay-to-win band is untouched because there is no purchase to make.
 *
 * **The HP dial is the SLOW one on purpose, and that is why it is the big one.** HP and damage
 * multiply together into combat power, so paying a rung mostly in HP buys *rounds* rather than
 * a kill: a fighter who evolves does not delete its opponent, it survives long enough to use
 * the ladder. That is the deliberate answer to the snowball risk the header names — the side
 * that is behind gets proportionally more out of the heal, and the side that is ahead gets a
 * longer fight to close out rather than a free one.
 */
export const EVOLUTION_RANK_HP_BONUS: Record<BattleEvolutionRank, number> = {
  1: 0,
  2: 0.2,
  3: 0.4,
};

/**
 * **WHAT A RUNG PAYS ON DAMAGE — the small, sharp dial.**
 *
 * **+15% per rung**, so Evolution 2 hits for 1.15x its printed numbers and Evolution 3 for 1.3x.
 * Applied to attack damage, and to magic damage or the special's value — never to both, see
 * {@link applyBattleEvolution}.
 *
 * **Deliberately a fraction of {@link EVOLUTION_RANK_HP_BONUS}** — three quarters of it, rung
 * for rung, on the owner's call. The two multiply into combat power, so the pair is what has to
 * be reasoned about rather than either alone: at the top rung that is 1.4x HP x 1.3x damage.
 * Loading more of it onto the bar is what keeps an evolution from being a round-ending burst —
 * a fighter that evolves twice takes noticeably longer to kill and hits somewhat harder.
 * Splitting the two dials is the whole point: they used to be one number and could not be
 * traded against each other at all.
 *
 * **Whatever this is set to, the round cannot exceed
 * {@link COMBAT_CONSTANTS.MAX_DAMAGE_MODIFIER}.** The rung and the ramp are clamped together by
 * `getEffectivePressureMultiplier`, so raising this dial spends the ceiling's headroom rather
 * than breaking through it — which is the property the drift described below went unnoticed for
 * a release precisely because nothing had.
 *
 * **IT WAS +20% A RUNG, WHICH IS NOT A FRACTION OF ANYTHING — it was the WHOLE of the HP dial.**
 * The two tables had drifted to identical values (`{2: 0.2, 3: 0.4}` in both), so the sentence
 * above was describing a split the numbers no longer had, and the "small, sharp dial" was the
 * same size as the big slow one. That is not a rounding error in a comment: because the rung is
 * reached on a CLOCK rather than as a choice — rank 3 is near-universal by round 8 — a full
 * 1.4x damage multiplier was riding on top of round pressure in every long fight, and the
 * product is what a player meets. Measured across 6,000 simulated battles, the attacker's
 * average evolution multiplier climbs 1.04x at round 1 to 1.40x by round 10, and
 * `getPressureMultiplier` takes the same round from 1.00x to 1.63x: **2.28x, of which pressure
 * — the only one of the two that is DESIGNED as a ramp, tuned in tuning-log Rows 52, 53 and 56
 * — is the smaller half.** Nothing measured the product, because criterion 21 divided by the
 * evolved bar and evolution cancelled itself out of its own measurement (see `turnFeel.ts`).
 *
 * Cutting it restores the split the design is written on and takes the top rung's contribution
 * from 1.40x to 1.30x (it was briefly 1.20x, before the owner set the rung at +15%). **The HP dial is untouched**, so an evolution keeps the identity the
 * module header argues for — the rescue, the longer fight, worth proportionally more to the
 * fighter who is behind — and loses only the burst it was never supposed to carry. Row 58 of
 * `docs/BATTLE_CARDS_TUNING_LOG.md` carries the paired before/after on every criterion.
 */
/**
 * **HOW MUCH OF THE POOL'S GROWTH IS HANDED BACK AS HP — all of it.**
 *
 * A bigger maximum on its own is worth nothing mid-fight: a fighter on 12 of 80 HP that evolves
 * into 12 of 160 has been handed a longer bar and a *smaller* share of it, which is a nerf
 * wearing a fanfare. So the growth is restored on the spot, and that is what makes an evolution
 * land as a rescue rather than as a number in a tooltip.
 *
 * **THE WHOLE OF IT, because the rule is "the HP an evolution grants is HP you have".** At 1.0 the
 * dial has one property nothing smaller has: **a fighter's share of its own bar can never FALL on
 * an evolution**, so a Suikamon at full health that evolves is still at full health. Every value
 * below 1 breaks that, and it broke it where the player can see it — at 0.5 a fighter on a full
 * 100 bar evolved into **150 of 200** and watched its own fill retreat to three quarters on the
 * beat it was rewarded. The game's biggest moment was drawn as damage taken, and it was reported
 * as a bug in the HP bar, which is exactly what it looks like.
 *
 * **The measurement the 0.5 was carrying did not survive being re-taken.** The share was cut from
 * 1.0 with the argument that a full heal stapled to a doubled pool was a win button, and most of
 * why `G4` read a median of **8** consecutive nodes cleared first-try-and-comfortable. Re-measured
 * at 0.5 on seeds 1337 and 42, **G4 still reads 8** — the cut bought nothing on the criterion it
 * was made for, and it was never traceable to a row in `docs/BATTLE_CARDS_TUNING_LOG.md` at all.
 * The row it finally has is Row 43, which records the paired before/after in full.
 *
 * **What actually bounds the mechanic is not this dial** (`requirements/BATTLE_EVOLUTION.md` §6):
 * the ladder stops at two evolutions, both fighters climb it from the same floor by the same rule,
 * and it all resets next battle. And the heal is still worth proportionally **more to the fighter
 * who is behind** — the same absolute HP is a larger share of what they had left — which is the
 * property that keeps the ladder from being a pure snowball. `battleEvolution.test.ts` asserts
 * both.
 *
 * Kept as a dial rather than folded away: it has moved twice, and the criterion it is watched on
 * is `13` (the winner's lowest bar), which passes with room but not with much.
 */
export const EVOLUTION_HEAL_SHARE = 1.0;

export const EVOLUTION_RANK_DAMAGE_BONUS: Record<BattleEvolutionRank, number> = {
  1: 0,
  2: 0.15,
  3: 0.3,
};

/**
 * The rung a fighter stands on, given how many cards it has taken this battle.
 *
 * Walked weakest-last, so a count above the top rung stays at the top rung. A negative or
 * non-finite count lands on {@link EVOLUTION_RANK_MIN} — this is read straight off a
 * deserialised `battle_state`, and a corrupt value must degrade to "no bonus" rather than to
 * `NaN` damage in a live fight.
 *
 * **`maxRank` is the fighter's own ceiling** — the one lever a campaign has for holding a rival
 * back (`CAMPAIGN_EVOLUTION_CAP` in `campaignConfig.ts`, where every rival that fights now
 * climbs the whole ladder and the escalation is carried by levels instead). It clamps the RANK rather
 * than the card count on purpose — a capped fighter still visibly collects cards, so a player
 * watching a boss fill its third pip learns what is about to happen, and the same fighter
 * re-read with a higher cap would simply be further along rather than starting over.
 *
 * Defaults to the top of the ladder, so a player — and anything that does not pass one — is
 * uncapped.
 */
export function getEvolutionRankForCards(
  cards: number,
  maxRank: BattleEvolutionRank = EVOLUTION_RANK_MAX
): BattleEvolutionRank {
  if (!Number.isFinite(cards)) return EVOLUTION_RANK_MIN;

  let rank: BattleEvolutionRank = EVOLUTION_RANK_MIN;
  for (const candidate of EVOLUTION_RANK_ORDER) {
    if (cards >= evolutionCardsForRank(candidate)) rank = candidate;
  }
  return rank > maxRank ? maxRank : rank;
}

/**
 * The multiplier a rung applies to the HP BAR — max HP, and the heal that comes with it.
 * Exactly 1 at Evolution 1, by definition.
 */
export function getEvolutionHpMultiplier(rank: BattleEvolutionRank): number {
  return 1 + (EVOLUTION_RANK_HP_BONUS[rank] ?? 0);
}

/**
 * The multiplier a rung applies to DAMAGE. Exactly 1 at Evolution 1, by definition.
 *
 * A separate function from {@link getEvolutionHpMultiplier} rather than one shared number,
 * because the two ladders pay differently and a caller that reached for "the" evolution
 * multiplier would silently get one of them.
 */
export function getEvolutionDamageMultiplier(rank: BattleEvolutionRank): number {
  return 1 + (EVOLUTION_RANK_DAMAGE_BONUS[rank] ?? 0);
}

/** Cards still needed for the next evolution, or `null` at the top of the ladder. */
export function cardsToNextEvolution(
  cards: number
): { rank: BattleEvolutionRank; needed: number } | null {
  const held = Number.isFinite(cards) ? cards : 0;
  for (const candidate of EVOLUTION_RANK_ORDER) {
    const required = evolutionCardsForRank(candidate);
    if (held < required) return { rank: candidate, needed: required - held };
  }
  return null;
}

/**
 * **THE FORM A FIGHTER WEARS AT THIS RUNG — the picture, and only the picture.**
 *
 * A Suikamon that battle-evolves takes the shape of its **own next stage** for the rest of the
 * fight: Evolution 2 wears stage + 1, Evolution 3 wears stage + 2. It is a preview of a form
 * this pet really has — the art the species already ships — shown for as long as the rung
 * lasts, and gone with it.
 *
 * **AT ITS LAST FORM IT KEEPS THE ONE IT HAS, AND STILL EARNS THE STATS.** A pet at
 * `maxStage` has no next picture to wear, so the clamp holds it where it is; the rung, the
 * damage, the HP and the heal are completely unaffected by any of this. The picture is a
 * reward, never a requirement — a fighter whose art cannot change is not a fighter who evolved
 * less.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **THIS STILL DOES NOT MOVE THE PET'S `stage`, AND MUST NOT.**
 *
 * `stage` is earned by LEVEL, written on-chain, and read by the Move contract's evolution
 * check (see the header of this module and `speciesConfig.ts`). Nothing here writes it: this
 * returns a number to draw with, the battle state stores no stage of its own, and every screen
 * outside a live fight — the collection, the profile, the card in your hand, the chain — reads
 * the pet's real stage exactly as before. A player who reloads mid-fight sees the previewed
 * form because the rank is in `battle_state`; a player who wins and goes to their collection
 * sees the Suikamon they actually own.
 */
export function getBattleFormStage(
  stage: number,
  rank: BattleEvolutionRank | undefined,
  maxStage: number
): number {
  const rungsClimbed = (rank ?? EVOLUTION_RANK_MIN) - EVOLUTION_RANK_MIN;
  if (!Number.isFinite(stage) || rungsClimbed <= 0) return stage;
  // A species whose `maxStage` is unknown (0, from `getMaxStage` on a missing id) must not be
  // clamped to nothing — better the pet's own art than an image that does not exist.
  if (!Number.isFinite(maxStage) || maxStage < stage) return stage;
  return Math.min(stage + rungsClimbed, maxStage);
}

// ============================================================================
// Applying it
// ============================================================================

/**
 * **The combat config this fighter is currently swinging, at its present evolution.**
 *
 * Scales exactly the stats `scaleCombatConfig` scales, and nothing else — the rule that keeps an
 * evolution from quietly re-tuning an archetype:
 *
 *  - **HP** always, at {@link getEvolutionHpMultiplier}. (The BAR; {@link growMaxHpForEvolution}
 *    moves the fighter's actual pool by the same dial.)
 *  - **Attack damage** always, at the SEPARATE and much smaller
 *    {@link getEvolutionDamageMultiplier} — the two ladders are not the same number and must
 *    never be collapsed back into one.
 *  - **Magic damage** for a species whose magic damage scales with level; **`specialValue`**
 *    instead for one whose does not. That branch is not invented here — it is
 *    `hasScalableSpecialEffect`, the existing single source of truth for the anti-double-scaling
 *    rule. A dot/drain/execute species keeps its magic damage flat and grows through its
 *    special, so an evolution has to follow it there or those species would evolve into nothing.
 *
 * **Recover heal is deliberately untouched**, and here that matters twice over: it is already
 * the slowest-scaling number in the game on purpose (`+0.1/level`, "minimal scaling to preserve
 * recover balance"), AND an evolution already hands out a heal of its own. Scaling recover on
 * top would pay the same fighter twice for the same rung. Shield is left alone for the same
 * reason it is in level scaling — it grows at a third of everyone else's rate, and the evolution
 * widens the HP pool it sits on regardless.
 *
 * **Idempotent from a fixed base**, which is what makes it safe to call every single turn: the
 * engine always derives from the config the battle was CREATED with, never from last turn's
 * result, so the multiplier lands on the base numbers once per call however many times it runs.
 * That is the same property `squadBattleService` relies on for the mode knobs.
 *
 * Returns the very same object at Evolution 1, so a fighter that never evolves is provably
 * fighting the game exactly as it was before this existed.
 */
export function applyBattleEvolution(
  combat: SpeciesCombatConfig,
  rank: BattleEvolutionRank | undefined
): SpeciesCombatConfig {
  const hpMultiplier = getEvolutionHpMultiplier(rank ?? EVOLUTION_RANK_MIN);
  const damageMultiplier = getEvolutionDamageMultiplier(rank ?? EVOLUTION_RANK_MIN);
  if (hpMultiplier === 1 && damageMultiplier === 1) return combat;

  // A 0-damage move (a heal-only Magic) stays 0 — scaling it would invent damage it never had.
  const scale = (value: number) => (value > 0 ? Math.round(value * damageMultiplier) : value);
  const growsThroughSpecial = hasScalableSpecialEffect(combat);

  return {
    ...combat,
    baseHp: Math.round(combat.baseHp * hpMultiplier),
    attack: { ...combat.attack, damage: scale(combat.attack.damage) },
    magic: {
      ...combat.magic,
      damage: growsThroughSpecial ? combat.magic.damage : scale(combat.magic.damage),
      // A species with no specialValue keeps NOT having one — scaling absence into a literal 0
      // would hand the engine a value where it expects nothing.
      specialValue:
        growsThroughSpecial && combat.magic.specialValue !== undefined
          ? scale(combat.magic.specialValue)
          : combat.magic.specialValue,
    },
  };
}

/** What an evolution does to a fighter's health pool. */
export interface EvolutionHpGrowth {
  /** The fighter's new maximum. */
  maxHp: number;
  /** The fighter's new current HP — the old one plus the share of the growth that was healed. */
  hp: number;
  /** How much bigger the pool got. Also exactly how much HP was restored. */
  gained: number;
}

/**
 * **THE EVOLUTION HEAL — why a rank-up is felt and not just read.**
 *
 * A bigger maximum on its own is worth nothing mid-fight: a fighter on 12 of 80 HP that evolves
 * into 12 of 160 has been handed a longer bar and a *smaller* share of it, which is a nerf
 * wearing a fanfare. So the growth is added to current HP as well — all of it
 * ({@link EVOLUTION_HEAL_SHARE}) — so a rung that grows the pool by a fifth of a bar **hands
 * that fifth back on the spot**, which is why it reads as a rescue and not just as a bigger
 * number.
 *
 * **The whole of it, so a fighter's SHARE of its own bar can never fall on an evolution.** That is
 * the one property a partial heal cannot have, and the reason it matters is not arithmetic: a
 * Suikamon at full health that evolved used to watch its own fill retreat to three quarters on the
 * beat it was rewarded, which reads as damage. The constant's own note carries the measurement
 * that took the dial back to 1.0.
 *
 * That is also what keeps the mechanic from being a pure snowball: the fighter who is behind
 * gets proportionally more out of the heal, because the same absolute HP is a larger share of
 * what they had left.
 *
 * **Computed from `baseMaxHp`, never from the current maximum**, so climbing 1 → 2 → 3 lands on
 * exactly the same pool as jumping straight to 3. Re-deriving off an already-scaled maximum
 * would compound the rounding at every rung and quietly drift the two apart.
 */
export function growMaxHpForEvolution(
  baseMaxHp: number,
  currentMaxHp: number,
  currentHp: number,
  rank: BattleEvolutionRank
): EvolutionHpGrowth {
  const maxHp = Math.round(baseMaxHp * getEvolutionHpMultiplier(rank));
  const gained = Math.max(0, maxHp - currentMaxHp);
  const healed = Math.round(gained * EVOLUTION_HEAL_SHARE);
  return { maxHp, gained: healed, hp: Math.min(currentHp + healed, maxHp) };
}

/**
 * **THE EVOLUTION BEAT — the one sentence a rung gets while it is happening.**
 *
 * A rung pays on TWO dials and the beat used to name one of them. It read `EVOLVED +14`, which
 * is the heal — the smaller half, and the half that is over the instant it lands. Everything
 * else the rung bought was silent: the pool doubled, the damage went up a fifth, and the player
 * was left with a bar that was inexplicably a different length and buttons whose numbers had
 * moved for no stated reason. "The user does not know what happened" was the exact report.
 *
 * So the beat states all three, in the order they matter to the fighter reading them: what
 * happened, what it gave back now, and what it changes from here.
 *
 * **It is written HERE, beside the dials it quotes**, because three modes spawn this same beat
 * (1v1, squad, campaign) and a fourth will. Three hand-copied templates are three chances for
 * the game to advertise a bonus it does not pay, and the percentage in particular is a
 * derivation — `EVOLUTION_RANK_DAMAGE_BONUS` is the only place that number is decided.
 *
 * Returns the two lines separately so a caller can lay them out; nothing here knows about CSS.
 */
export interface EvolutionBeatText {
  /** The event, in one word plus the rung it reached. */
  headline: string;
  /** What it paid: the HP restored now, and the damage it buys for the rest of the fight. */
  detail: string;
}

export function describeEvolutionBeat(
  healed: number,
  rank: BattleEvolutionRank
): EvolutionBeatText {
  const damagePercent = Math.round((getEvolutionDamageMultiplier(rank) - 1) * 100);
  return {
    // Named the way the nameplate's chip names it, so the top rung is one thing with one word
    // wherever a player meets it.
    headline: rank >= EVOLUTION_RANK_MAX ? 'MAX EVO!' : 'EVOLVED!',
    detail: `+${healed} HP · +${damagePercent}% DMG`,
  };
}

/**
 * **WHO TAKES A CARD THIS TURN — the whole earning rule, in one place.**
 *
 * The fighter who came out of the exchange ahead takes a Suikamon card off the one who did not.
 * "Ahead" is simply *took less damage this turn*, which is already the number the engine
 * computes for every other purpose, so there is no second notion of winning a round to keep in
 * sync with the first.
 *
 * **A tied exchange pays nobody** — including the very common 0-0 turn where both sides dodged
 * or both were blocked. A card handed out for a turn where nothing happened would put every
 * fight on a fixed evolution timer and delete the decision entirely.
 *
 * Takes the two damage figures rather than the states, so it stays pure and the engine can call
 * it at the one point both are final.
 */
export function whoTakesCard(
  challengerDamageTaken: number,
  defenderDamageTaken: number
): 'challenger' | 'defender' | null {
  if (challengerDamageTaken === defenderDamageTaken) return null;
  return challengerDamageTaken < defenderDamageTaken ? 'challenger' : 'defender';
}
